import { useCallback, useEffect, useMemo, useState } from "react";

import { useHomeContext } from "app/pages/activation/context/Home/HomeContext";
import { useActionableInsightsQuery } from "app/pages/activation/queries/homeWithData/useActionableInsightsQuery";
import { activationTracking } from "app/pages/activation/services/tracking";
import type { HomepageInsightTrackItem } from "app/pages/activation/services/tracking/home/homepageInsights";
import type { ActionableInsight } from "app/pages/activation/types/api";

import {
    buildActionableInsightsParams,
    isActionableInsightsQueryEnabled,
} from "./helpers/actionableInsightsQueryParams";
import { buildInsightCopyPlainText } from "./helpers/buildInsightCopyPlainText";

export const useInsightsSection = () => {
    const { activeActivation } = useHomeContext();
    const params = useMemo(() => buildActionableInsightsParams(activeActivation), [activeActivation]);
    const enabled = isActionableInsightsQueryEnabled(params);
    const tracking = activationTracking.homePageInsights;

    const { data, isLoading, isError } = useActionableInsightsQuery(params, enabled);

    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [openRowIndex, setOpenRowIndex] = useState<number | null>(null);

    useEffect(() => {
        if (!data?.length) {
            return;
        }

        const insightsTrackingData: HomepageInsightTrackItem[] = data.map((item, index) => ({
            fullText: buildInsightCopyPlainText(item),
            insightNumber: index + 1,
        }));

        tracking.trackInsightsSectionView({
            insightCount: data.length,
            insights: insightsTrackingData,
        });
    }, [data, tracking]);

    useEffect(() => {
        setOpenRowIndex(null);
    }, [activeActivation?.id]);

    const onTogglePanel = useCallback(
        (nextOpen: boolean) => {
            if (nextOpen) {
                tracking.trackInsightExpand();
            } else {
                tracking.trackInsightCollapse();
            }
            setIsPanelOpen(nextOpen);
        },
        [tracking],
    );

    const onToggleInsightRow = useCallback(
        (index: number, nextOpen: boolean, insight: ActionableInsight) => {
            const insightNumber = index + 1;
            const fullText = buildInsightCopyPlainText(insight);

            if (nextOpen) {
                tracking.trackInsightRowClick({ insightNumber, fullText });
            } else {
                tracking.trackInsightRowClose({ insightNumber, fullText });
            }

            setOpenRowIndex(nextOpen ? index : null);
        },
        [tracking],
    );

    const shouldShowSection = isLoading || (enabled && !isError && Boolean(data?.length));

    return {
        data: data ?? [],
        isLoading,
        isPanelOpen,
        onTogglePanel,
        openRowIndex,
        onToggleInsightRow,
        shouldShowSection,
    };
};
